import PropTypes from "prop-types";
import React, { Component } from "react";
import { graphql } from "@apollo/client/react/hoc";
import compose from "lodash.flowright";
import { withRouter } from "react-router-dom";

import { GET_AUTH } from "../../../graphql/resolvers/auth";
import { isLoggedIn } from "../../../helpers/auth";
import {
  releaseBuildYourOwnReservation,
  useBuildYourOwnAddGuard,
} from "../../../helpers/buildYourOwnCartLock";
import { FETCH_POLICY_CACHE_ONLY } from "../../../helpers/constants";
import { logError } from "../../../helpers/logging";
import { GET_MEMBER_SHOPPING_CART } from "../../../graphql/queries";
import {
  ADD_SHOPPING_CART_ITEM_BULK,
  ADD_ITEMS_TO_LOCAL_SHOPPING_CART,
  SHOW_LOCAL_SHOPPING_CART,
  DELETE_ITEMS_BY_PRODUCT_TYPE_IN_LOCAL_SHOPPING_CART,
  DELETE_PRODUCT_TYPE_SHOPPING_CART_ITEMS,
} from "../../../graphql/mutations";

import Button from "../Button";
import { showErrorAlert } from "../../../helpers/alerts";
import { AppContext } from "../../organisms/Layout/context";

/**
 * Renders AddProductsToShoppingCartButton component that allows adding product to a Shopping Cart.
 * */
class AddProductsToShoppingCartButton extends Component {
  static contextType = AppContext;

  static propTypes = {
    products: PropTypes.arrayOf(
      PropTypes.shape({
        product: PropTypes.shape({}).isRequired,
        quantity: PropTypes.number,
      })
    ).isRequired,
    label: PropTypes.string,
    authQuery: PropTypes.shape({}).isRequired,
    showShoppingCart: PropTypes.bool,
    showLocalShoppingCart: PropTypes.func.isRequired,
    callbackOnAddSuccess: PropTypes.func,
    callbackOnAddFail: PropTypes.func,
    addShoppingCartItems: PropTypes.func.isRequired,
    className: PropTypes.string,
    children: PropTypes.node,
    disabled: PropTypes.bool,
    extraInfo: PropTypes.shape({}),
    deleteSameProductType: PropTypes.bool,
    deleteItemsByProductTypeInLocalShoppingCart: PropTypes.func.isRequired,
    deleteProductTypeShoppingCartItems: PropTypes.func.isRequired,
    // Stops an add that a build-your-own advent order (wines only) cannot take, explaining why.
    isBlockedByBuildYourOwn: PropTypes.func,
  };

  static defaultProps = {
    label: "Add",
    showShoppingCart: true,
    callbackOnAddSuccess: null,
    callbackOnAddFail: null,
    className: "",
    children: null,
    disabled: false,
    extraInfo: null,
    deleteSameProductType: false,
  };

  handlePayLoad = (items) =>
    items.map((item) => ({
      productId: item.product.id,
      quantity: item.quantity,
    }));

  /**
   * Adds items to shopping cart
   * If user IS logged in, adds item to database's shopping cart
   * If user IS NOT logged in, adds item to browser local storage's shopping cart
   * @param {Array} items - products to be added
   * @return {Promise<void>}
   * */
  handleAddItemsToShoppingCart = async (items) => {
    const promises = items.map((item) => this.resolvePreSaveOperations(item));

    Promise.all(promises).then(() => {
      const { authQuery, addShoppingCartItems, addItemsToLocalShoppingCart } = this.props;
      const { minBottlesForFreeShipping, standardShippingFee } = this.context;

      if (isLoggedIn()) {
        const memberId = authQuery.auth && authQuery.auth.memberId;
        if (!memberId) {
          this.releaseGuardedItems();
          logError("User is logged in, but does not have a memberId in local storage.");
        } else {
          const newItems = this.handlePayLoad(items);

          // Saves product to shopping cart database in case user is logged in
          addShoppingCartItems({
            variables: {
              input: {
                memberId,
                items: newItems,
              },
            },
          })
            .then(({ data }) => {
              this.finaliseOperation(data);
            })
            .catch((error) => {
              this.finaliseOperationWithError(error);
            });
        }
      } else {
        // Saves product item to shopping cart in browser session in case user is not logged in
        const shoppingCartItem = {
          variables: {
            input: { minBottlesForFreeShipping, standardShippingFee, items },
          },
        };
        addItemsToLocalShoppingCart(shoppingCartItem)
          .then((response) => {
            if (response.errors?.length) throw response.errors[0];
            this.finaliseOperation();
          })
          .catch((error) => {
            if (this.props.callbackOnAddFail) {
              this.finaliseOperationWithError(error);
            } else {
              this.releaseGuardedItems();
              logError(error);
              showErrorAlert("Sorry, something went wrong.");
            }
          });
      }
    });
  };

  /**
   * Executes all callbacks after product has been successfully added to the Shopping Cart.
   */
  finaliseOperation = (data) => {
    const { showShoppingCart, showLocalShoppingCart, callbackOnAddSuccess } = this.props;
    showShoppingCart && showLocalShoppingCart();
    callbackOnAddSuccess && callbackOnAddSuccess(data);
  };

  /**
   * Executes all callbacks after product has been successfully added to the Shopping Cart.
   */
  releaseGuardedItems = () => {
    if (!this.guardedItems) return;
    releaseBuildYourOwnReservation(this.guardedItems);
    this.guardedItems = null;
  };

  finaliseOperationWithError = (error) => {
    this.releaseGuardedItems();
    const { callbackOnAddFail } = this.props;
    callbackOnAddFail && callbackOnAddFail(error);
  };

  /**
   * Make any operations required before adding a product to the shopping cart.
   * @param {Object} item - item with product to be added
   * @return {Promise<void>}
   * */
  resolvePreSaveOperations = (item) => this.deleteItemsFromTheSameProductType(item);

  /**
   * Deletes any items from the shopping cart of the same productTypeId.
   * @param {Object} item - item with product to be added
   * @return {Promise<void>}
   * */
  deleteItemsFromTheSameProductType = (item) => {
    const {
      authQuery,
      deleteSameProductType,
      deleteProductTypeShoppingCartItems,
      deleteItemsByProductTypeInLocalShoppingCart,
    } = this.props;
    const { minBottlesForFreeShipping, standardShippingFee } = this.context;

    if (deleteSameProductType) {
      if (isLoggedIn()) {
        const memberId = authQuery.auth && authQuery.auth.memberId;
        if (!memberId) {
          logError("User is logged in, but does not have a memberId in local storage.");
        } else {
          // Deletes all items from the given Product Type from the member's shopping cart
          return deleteProductTypeShoppingCartItems({
            variables: {
              input: {
                memberId,
                productTypeId: item.product.productType.id,
              },
            },
          });
        }
      } else {
        // Deletes all items from the given productTypeId in the local shopping cart
        return deleteItemsByProductTypeInLocalShoppingCart({
          variables: {
            input: {
              productTypeId: item.product.productType.id,
              minBottlesForFreeShipping,
              standardShippingFee,
            },
          },
        });
      }
    }
    return Promise.resolve();
  };

  render() {
    const { products, label, className, children, onClick, ...otherProps } = this.props;

    return (
      <Button
        className={className}
        type="button"
        onClick={() => {
          // A build-your-own order (wines only, one calendar) may refuse the add; the guard says
          // why. Ask before the caller's onClick, which trays use to show their loading state.
          const { isBlockedByBuildYourOwn } = this.props;
          if (isBlockedByBuildYourOwn && isBlockedByBuildYourOwn(products)) return;
          // Wine the guard let through is spoken for until the cart shows it; a failed add hands it back.
          this.guardedItems = products;
          onClick && onClick();
          this.handleAddItemsToShoppingCart(products);
        }}
        {...otherProps}
      >
        {label}
        {children}
      </Button>
    );
  }
}

const AddProductsToShoppingCartButtonWrapper = (props) => {
  const isBlockedByBuildYourOwn = useBuildYourOwnAddGuard();
  return <AddProductsToShoppingCartButton isBlockedByBuildYourOwn={isBlockedByBuildYourOwn} {...props} />;
};

export default compose(
  withRouter,
  graphql(GET_AUTH, {
    name: "authQuery",
    options: { fetchPolicy: FETCH_POLICY_CACHE_ONLY },
  }),
  graphql(ADD_SHOPPING_CART_ITEM_BULK, {
    name: "addShoppingCartItems",
    options: { refetchQueries: () => [{ query: GET_MEMBER_SHOPPING_CART }] },
  }),
  graphql(ADD_ITEMS_TO_LOCAL_SHOPPING_CART, {
    name: "addItemsToLocalShoppingCart",
  }),
  graphql(SHOW_LOCAL_SHOPPING_CART, { name: "showLocalShoppingCart" }),
  graphql(DELETE_ITEMS_BY_PRODUCT_TYPE_IN_LOCAL_SHOPPING_CART, {
    name: "deleteItemsByProductTypeInLocalShoppingCart",
  }),
  graphql(DELETE_PRODUCT_TYPE_SHOPPING_CART_ITEMS, {
    name: "deleteProductTypeShoppingCartItems",
    options: { refetchQueries: () => [{ query: GET_MEMBER_SHOPPING_CART }] },
  })
)(AddProductsToShoppingCartButtonWrapper);
